// Фильтры раздела «Правила» строкой чипов.
//
// Прежде фильтры занимали отдельную колонку во всю высоту, и карте оставалась
// четверть экрана. Чип показывает выбранное значение и крестик сброса, само
// значение выбирается в поповере под чипом. Слова видов, орудий и видов
// рыболовства — СЛОВА ПРИКАЗА (данные, на русском), подписи — ключи каталога.

import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useLang, useT } from '../i18n'
import type { RulesResponse } from '../api'
import type { RulesUrlState } from './rulesUrl'
import { termLabel } from './terms'

type Patch = (p: Partial<RulesUrlState>) => void

/** Поповер под чипом: закрывается кликом мимо и клавишей Esc. */
function Chip({ label, value, onClear, children }: {
  label: string
  value: string | null
  onClear: () => void
  children: (close: () => void) => ReactNode
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const box = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!open) return
    const down = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', down)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('mousedown', down)
      document.removeEventListener('keydown', key)
    }
  }, [open])
  const set = value != null && value !== ''
  return (
    <div ref={box} className="relative">
      <div className={`flex items-center rounded-full border text-[12px] ${
        set ? 'border-sonar bg-sonar/15 text-foam' : 'border-hull bg-deep text-mist'}`}>
        <button type="button" aria-expanded={open} onClick={() => setOpen(o => !o)}
          className="max-w-[180px] truncate rounded-full px-2.5 py-1 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar">
          {set ? value : label}
        </button>
        {set && (
          <button type="button" onClick={onClear} aria-label={t('rules.chip.clear')}
            className="-ml-1 rounded-full px-1.5 py-1 text-mist hover:text-foam focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar">
            ×
          </button>
        )}
      </div>
      {open && (
        <div role="dialog" aria-label={label}
          className="absolute left-0 top-full z-20 mt-1 w-[260px] rounded-xl border border-hull bg-trench p-2 text-[12px] shadow-console">
          <div className="mb-1.5 text-faint">{label}</div>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  )
}

/** Список значений с поиском: видов в каталоге больше сотни. */
function Pick({ options, value, onPick, render }: {
  options: string[]
  value: string | null
  onPick: (v: string | null) => void
  render?: (v: string) => string
}) {
  const t = useT()
  const [q, setQ] = useState('')
  const shown = useMemo(() => {
    const w = q.trim().toLowerCase()
    return w ? options.filter(o => (render ? render(o) : o).toLowerCase().includes(w)) : options
  }, [options, q, render])
  return (
    <div>
      {options.length > 8 && (
        <input autoFocus type="search" value={q} onChange={e => setQ(e.target.value)}
          placeholder={t('rules.chip.find')}
          className="mb-1.5 w-full rounded border border-hull bg-deep px-1.5 py-1 text-foam" />
      )}
      <ul className="max-h-[240px] overflow-y-auto">
        <li>
          <button type="button" onClick={() => onPick(null)}
            className={`w-full rounded px-1.5 py-1 text-left hover:bg-haze ${value == null ? 'text-sonar' : 'text-mist'}`}>
            {t('rules.filter.any')}
          </button>
        </li>
        {shown.map(o => (
          <li key={o}>
            <button type="button" onClick={() => onPick(o)}
              className={`w-full rounded px-1.5 py-1 text-left hover:bg-haze ${value === o ? 'text-sonar' : 'text-foam'}`}>
              {render ? render(o) : o}
            </button>
          </li>
        ))}
      </ul>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-faint">{label}</span>
      <div className="mt-0.5">{children}</div>
    </label>
  )
}

const INPUT = 'w-full rounded border border-hull bg-deep px-1.5 py-1 text-foam'

export function FilterChips({ state, facets, onChange }: {
  state: RulesUrlState
  facets: RulesResponse['facets'] | undefined
  onChange: Patch
}) {
  const t = useT()
  const { lang } = useLang()
  const sp = (k: string) => termLabel(k, facets?.species_terms, lang) ?? k
  const gr = (k: string) => termLabel(k, facets?.gear_terms, lang) ?? k
  // Аудитория — кодом; старая ссылка `?ft=любительское` показывается словом.
  const ftName = (v: string) => (facets?.audiences?.includes(v) ? t(`rules.audience.${v}`) : v)
  const more = [state.section, state.kind, state.fishingType, state.onlyActive || null]
    .filter(v => v != null && v !== '').length
  const kindName = (k: string) => t(`rules.kind.${k}`)
  return (
    <div className="flex flex-wrap gap-1.5 max-md:flex-nowrap max-md:overflow-x-auto">
      <Chip label={t('rules.filter.species')} value={state.species && sp(state.species)}
        onClear={() => onChange({ species: null })}>
        {close => <Pick options={facets?.species ?? []} value={state.species} render={sp}
          onPick={v => { onChange({ species: v }); close() }} />}
      </Chip>
      <Chip label={t('rules.filter.gear')} value={state.gear && gr(state.gear)}
        onClear={() => onChange({ gear: null })}>
        {close => <Pick options={facets?.gears ?? []} value={state.gear} render={gr}
          onPick={v => { onChange({ gear: v }); close() }} />}
      </Chip>
      <Chip label={t('rules.chip.vessel')}
        value={state.vesselLength != null ? t('rules.chip.vesselValue', { m: state.vesselLength }) : null}
        onClear={() => onChange({ vesselLength: null })}>
        {() => (
          <Field label={t('rules.filter.vesselLength')}>
            <input autoFocus type="number" min={0} max={500} step={1} className={INPUT}
              value={state.vesselLength ?? ''}
              onChange={e => {
                const n = e.target.value === '' ? null : Number(e.target.value)
                onChange({ vesselLength: n != null && Number.isFinite(n) && n >= 0 ? n : null })
              }} />
          </Field>
        )}
      </Chip>
      <Chip label={t('rules.chip.today')} value={state.date}
        onClear={() => onChange({ date: null })}>
        {() => (
          <Field label={t('rules.filter.date')}>
            <input autoFocus type="date" className={INPUT} value={state.date ?? ''}
              onChange={e => onChange({ date: e.target.value || null })} />
          </Field>
        )}
      </Chip>
      <Chip label={t('rules.chip.more')}
        value={more ? t('rules.chip.moreValue', { count: more }) : null}
        onClear={() => onChange({ section: null, kind: null, fishingType: null, onlyActive: false })}>
        {() => (
          <div className="space-y-2">
            <Field label={t('rules.filter.section')}>
              <select className={INPUT} value={state.section ?? ''}
                onChange={e => onChange({ section: e.target.value || null })}>
                <option value="">{t('rules.filter.any')}</option>
                {(facets?.sections ?? []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label={t('rules.filter.kind')}>
              <select className={INPUT} value={state.kind ?? ''}
                onChange={e => onChange({ kind: e.target.value || null })}>
                <option value="">{t('rules.filter.any')}</option>
                {(facets?.kinds ?? []).map(o => <option key={o} value={o}>{kindName(o)}</option>)}
              </select>
            </Field>
            <Field label={t('rules.filter.fishingType')}>
              <select className={INPUT} value={state.fishingType ?? ''}
                onChange={e => onChange({ fishingType: e.target.value || null })}>
                <option value="">{t('rules.filter.any')}</option>
                {(facets?.audiences ?? facets?.fishing_types ?? []).map(o =>
                  <option key={o} value={o}>{ftName(o)}</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-mist">
              <input type="checkbox" checked={state.onlyActive}
                onChange={() => onChange({ onlyActive: !state.onlyActive })} />
              <span>{t('rules.filter.onlyActive')}</span>
            </label>
          </div>
        )}
      </Chip>
    </div>
  )
}
